import { Alert, Box, Button, CircularProgress, Stack } from "@mui/material";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import type { ScoutDataChangeActors, ScoutDataChangeSummary } from "../hooks/types";

import {
  DATA_CHANGE_PROPOSAL_KIND,
  canUndoDataChange,
  formatDataChangeProposalTitle,
  formatUndoChangesLabel,
  nextUndoExpiryIso,
  type ScoutDataChangeCardStatus,
  type ScoutDataChangeProposal,
  type ScoutDataChangeProposalOperation,
} from "./scoutDataChangeProposal";
import { ScoutDataChangeProposalCard } from "./ScoutDataChangeProposalCard";
import {
  collectUndoResultFailures,
  findUndoResultFailure,
  formatBatchUndoFailureDisplay,
  formatUndoFailureDisplay,
  persistedUndoFailure,
  ScoutDataChangeUndoError,
  staleUndoFailureChangeIds,
  terminalCardStatusForUndoResult,
  type ScoutDataChangeUndoResult,
  type UndoFailureDisplay,
  type UndoTerminalCardStatus,
} from "./scoutDataChangeUndoFailure";
import {
  ScoutDataChangeUndoFailureAlert,
  ShowUndoErrorButton,
} from "./ScoutDataChangeUndoFailureAlert";
import {
  useScoutProposalActionsDisabled,
  useScoutProposalHandlers,
  useScoutProposalSurfaceReadOnly,
  type ScoutProposalResponseStatus,
} from "./ScoutProposalContext";

type LocalDecision = {
  status: ScoutProposalResponseStatus;
  title: string;
  operation?: ScoutDataChangeProposalOperation;
};

function resolvedDataChangeCardStatus(input: {
  proposalId: string;
  respondedProposals: Record<string, ScoutDataChangeCardStatus> | undefined;
  localUndoStatuses: Record<string, UndoTerminalCardStatus>;
  localDecision?: LocalDecision;
  rowStatus?: ScoutDataChangeCardStatus;
}): ScoutDataChangeCardStatus | undefined {
  const fromHistory = input.respondedProposals?.[input.proposalId];
  const fromUndo = input.localUndoStatuses[input.proposalId];
  // Overlay (history / SSE / undo mutation) is source of truth. Local undo
  // statuses only paint the chip before the store ticks.
  if (fromUndo && (fromHistory === undefined || fromHistory === "applied")) {
    return fromUndo;
  }
  return fromHistory ?? input.rowStatus ?? input.localDecision?.status;
}

function undoOverlaysFromResults(results: ScoutDataChangeUndoResult[] | void): {
  overlays: Record<string, UndoTerminalCardStatus>;
  terminalIds: string[];
} {
  const overlays: Record<string, UndoTerminalCardStatus> = {};
  const terminalIds: string[] = [];
  if (!results) {
    return { overlays, terminalIds };
  }
  for (const result of results) {
    const overlay = terminalCardStatusForUndoResult(result);
    if (!overlay) continue;
    overlays[result.changeId] = overlay;
    if (!terminalIds.includes(result.changeId)) {
      terminalIds.push(result.changeId);
    }
  }
  return { overlays, terminalIds };
}

type Props = {
  proposals: ScoutDataChangeProposal[];
};

type SubmittingAction = "confirm-all" | "decline-all" | "flush" | null;

type ResolveItem = {
  proposalId: string;
  title: string;
  status: ScoutProposalResponseStatus;
  operation?: ScoutDataChangeProposalOperation;
};

function useUndoClock(summaries: Iterable<ScoutDataChangeSummary>): number {
  const [nowMs, setNowMs] = useState(() => Date.now());
  const nextExpiry = nextUndoExpiryIso([...summaries], nowMs);

  useEffect(() => {
    if (!nextExpiry) return;
    const expiresAtMs = new Date(nextExpiry).getTime();
    if (!Number.isFinite(expiresAtMs)) return;
    const remainingMs = expiresAtMs - Date.now();
    if (remainingMs <= 0) return;
    const timer = setTimeout(() => setNowMs(Date.now()), remainingMs);
    return () => clearTimeout(timer);
  }, [nextExpiry]);

  return nowMs;
}

function useDataChangeStackUndo(input: {
  proposals: ScoutDataChangeProposal[];
  summaries: ScoutDataChangeSummary[];
  isReadOnly: boolean;
  isSubmitting: boolean;
  turnActionsDisabled: boolean;
  onUndo?: (changeIds: string[]) => Promise<ScoutDataChangeUndoResult[] | void>;
}): {
  eligibleUndoIds: string[];
  inFlightUndoIds: string[];
  showUndoAll: boolean;
  undoAllBusy: boolean;
  batchUndoDisplay: UndoFailureDisplay | undefined;
  hasHiddenBatchUndoError: boolean;
  batchStaleRetryIds: string[];
  /** Ids whose failures the visible batch alert reports; their cards defer to it. */
  batchReportedUndoIds: string[];
  localUndoStatuses: Record<string, UndoTerminalCardStatus>;
  undoOne: (changeId: string) => Promise<void>;
  undoAll: () => Promise<void>;
  dismissBatchUndoError: () => void;
  showBatchUndoError: () => void;
} {
  const { proposals, summaries, isReadOnly, isSubmitting, turnActionsDisabled, onUndo } = input;
  const [hiddenUndoIds, setHiddenUndoIds] = useState<string[]>([]);
  const [inFlightUndoIds, setInFlightUndoIds] = useState<string[]>([]);
  const [undoAllBusy, setUndoAllBusy] = useState(false);
  const [batchUndoFailures, setBatchUndoFailures] = useState<ScoutDataChangeUndoResult[]>([]);
  const [batchUndoThrown, setBatchUndoThrown] = useState<unknown>(null);
  const [batchUndoIds, setBatchUndoIds] = useState<string[]>([]);
  const [batchUndoErrorHidden, setBatchUndoErrorHidden] = useState(false);
  const [localUndoStatuses, setLocalUndoStatuses] = useState<
    Record<string, UndoTerminalCardStatus>
  >({});

  const summariesById = useMemo(() => {
    const byId = new Map<string, ScoutDataChangeSummary>();
    for (const summary of summaries) {
      byId.set(summary.id, summary);
    }
    return byId;
  }, [summaries]);

  const nowMs = useUndoClock(summariesById.values());
  const hiddenUndoIdSet = useMemo(() => new Set(hiddenUndoIds), [hiddenUndoIds]);

  const eligibleUndoIds = useMemo(() => {
    if (isReadOnly || !onUndo || turnActionsDisabled) {
      return [];
    }
    const ids: string[] = [];
    for (const proposal of proposals) {
      if (hiddenUndoIdSet.has(proposal.proposalId)) continue;
      if (!canUndoDataChange(summariesById.get(proposal.proposalId), nowMs)) continue;
      ids.push(proposal.proposalId);
    }
    return ids;
  }, [hiddenUndoIdSet, isReadOnly, nowMs, onUndo, proposals, summariesById, turnActionsDisabled]);

  const applyUndoResultOverlays = (results: ScoutDataChangeUndoResult[] | void): string[] => {
    const { overlays, terminalIds } = undoOverlaysFromResults(results);
    if (terminalIds.length === 0) return [];
    setLocalUndoStatuses((current) => ({ ...current, ...overlays }));
    setHiddenUndoIds((current) => [...new Set([...current, ...terminalIds])]);
    return terminalIds;
  };

  const undoOne = async (changeId: string) => {
    if (!onUndo) return;
    setBatchUndoFailures([]);
    setBatchUndoThrown(null);
    setBatchUndoErrorHidden(false);
    setHiddenUndoIds((current) => (current.includes(changeId) ? current : [...current, changeId]));
    setInFlightUndoIds((current) =>
      current.includes(changeId) ? current : [...current, changeId]
    );
    let persistedTerminal = false;
    try {
      const results = await onUndo([changeId]);
      persistedTerminal = applyUndoResultOverlays(results).includes(changeId);
      const failure = findUndoResultFailure(results, changeId);
      if (failure) {
        throw new ScoutDataChangeUndoError(failure);
      }
    } catch (error) {
      if (!persistedTerminal) {
        setHiddenUndoIds((current) => current.filter((id) => id !== changeId));
      }
      throw error;
    } finally {
      setInFlightUndoIds((current) => current.filter((id) => id !== changeId));
    }
  };

  const undoAll = async () => {
    if (!onUndo || isSubmitting || turnActionsDisabled || eligibleUndoIds.length === 0) {
      return;
    }
    const ids = eligibleUndoIds;
    setBatchUndoFailures([]);
    setBatchUndoThrown(null);
    setBatchUndoErrorHidden(false);
    setBatchUndoIds(ids);
    setUndoAllBusy(true);
    try {
      const results = await onUndo(ids);
      applyUndoResultOverlays(results);
      const failures = collectUndoResultFailures(results, ids);
      const failedIds = failures.map((failure) => failure.changeId);
      const succeededIds = ids.filter((changeId) => !failedIds.includes(changeId));
      if (succeededIds.length > 0) {
        setHiddenUndoIds((current) => [...new Set([...current, ...succeededIds])]);
      }
      if (failures.length > 0) {
        setBatchUndoFailures(failures);
      }
    } catch (error) {
      setBatchUndoThrown(error);
    } finally {
      setUndoAllBusy(false);
    }
  };

  const batchUndoDisplay = batchUndoThrown
    ? formatUndoFailureDisplay(batchUndoThrown, { changeCount: batchUndoIds.length })
    : formatBatchUndoFailureDisplay(batchUndoFailures);
  const visibleBatchUndoDisplay = batchUndoErrorHidden ? undefined : batchUndoDisplay;

  return {
    eligibleUndoIds,
    inFlightUndoIds,
    showUndoAll: eligibleUndoIds.length > 1 || undoAllBusy,
    undoAllBusy,
    batchUndoDisplay: visibleBatchUndoDisplay,
    hasHiddenBatchUndoError: Boolean(batchUndoDisplay) && batchUndoErrorHidden,
    batchStaleRetryIds: staleUndoFailureChangeIds(batchUndoFailures),
    batchReportedUndoIds: visibleBatchUndoDisplay ? batchUndoIds : [],
    localUndoStatuses,
    undoOne,
    undoAll,
    dismissBatchUndoError: () => setBatchUndoErrorHidden(true),
    showBatchUndoError: () => setBatchUndoErrorHidden(false),
  };
}

function rowStatusByProposalId(
  proposals: ReadonlyArray<ScoutDataChangeProposal>
): Map<string, ScoutDataChangeCardStatus> {
  const byId = new Map<string, ScoutDataChangeCardStatus>();
  for (const proposal of proposals) {
    if (proposal.rowStatus) {
      byId.set(proposal.proposalId, proposal.rowStatus);
    }
  }
  return byId;
}

function staffHistoryActors(
  summary: ScoutDataChangeSummary | undefined
): ScoutDataChangeActors | undefined {
  if (!summary) {
    return undefined;
  }
  const actors = {
    proposedBy: summary.proposedBy,
    confirmedBy: summary.confirmedBy,
    declinedBy: summary.declinedBy,
    appliedBy: summary.appliedBy,
    undoneBy: summary.undoneBy,
  };
  if (
    !actors.proposedBy &&
    !actors.confirmedBy &&
    !actors.declinedBy &&
    !actors.appliedBy &&
    !actors.undoneBy
  ) {
    return undefined;
  }
  return actors;
}

/**
 * Stack of staged data-change cards under a tool group.
 *
 * Per-card Accept/Reject decisions are held locally until every card in the
 * stack is decided (or Accept all / Reject all runs). Then one synthetic
 * continue message is sent — so mixed Accept-then-Reject never races a second
 * Scout send against an active turn.
 */
export function ScoutDataChangeProposalStack({ proposals }: Props) {
  const handlers = useScoutProposalHandlers(DATA_CHANGE_PROPOSAL_KIND);
  const surfaceReadOnly = useScoutProposalSurfaceReadOnly();
  const turnActionsDisabled = useScoutProposalActionsDisabled();
  const [hasError, setHasError] = useState(false);
  const [submittingAction, setSubmittingAction] = useState<SubmittingAction>(null);
  const [localDecisions, setLocalDecisions] = useState<Record<string, LocalDecision>>({});
  // Ref mirrors localDecisions so rapid per-card clicks see each other before
  // the next React render commits (Accept then Reject on siblings).
  const localDecisionsRef = useRef(localDecisions);
  localDecisionsRef.current = localDecisions;

  const hasHandlers = handlers !== undefined;
  const isReadOnly = surfaceReadOnly || !hasHandlers;
  const resolveBusy = submittingAction !== null || (handlers?.isProcessing ?? false);
  const retryStaleUndo = handlers?.onRetryStaleUndo;
  const stackUndo = useDataChangeStackUndo({
    proposals,
    summaries: handlers?.dataChanges ?? [],
    isReadOnly,
    isSubmitting: resolveBusy,
    turnActionsDisabled,
    onUndo: handlers?.onUndo,
  });

  const rowStatusById = useMemo(() => rowStatusByProposalId(proposals), [proposals]);
  const summariesById = useMemo(() => {
    const byId = new Map<string, ScoutDataChangeSummary>();
    for (const summary of handlers?.dataChanges ?? []) {
      byId.set(summary.id, summary);
    }
    return byId;
  }, [handlers?.dataChanges]);

  const getStatus = useCallback(
    (proposalId: string): ScoutDataChangeCardStatus | undefined => {
      return resolvedDataChangeCardStatus({
        proposalId,
        respondedProposals: handlers?.respondedProposals,
        localUndoStatuses: stackUndo.localUndoStatuses,
        localDecision: localDecisions[proposalId],
        rowStatus: rowStatusById.get(proposalId),
      });
    },
    [handlers, localDecisions, rowStatusById, stackUndo.localUndoStatuses]
  );

  const pendingProposals = useMemo(() => {
    return proposals.filter((proposal) => getStatus(proposal.proposalId) === undefined);
  }, [getStatus, proposals]);

  const showStackActions = !isReadOnly && proposals.length > 1 && pendingProposals.length > 0;
  const isSubmitting = resolveBusy || stackUndo.undoAllBusy;
  // Keep Accept/Reject locked while the agent turn is still streaming so a
  // multi-chunk batch cannot be resolved before every card has been staged.
  const isLockedOut = isSubmitting || !hasHandlers || turnActionsDisabled;

  const flushResolutions = useCallback(
    async (items: ResolveItem[]) => {
      if (!handlers || items.length === 0) return;
      setHasError(false);
      setSubmittingAction("flush");
      try {
        await handlers.onResolve({ proposals: items });
        setLocalDecisions((current) => {
          const next = { ...current };
          for (const item of items) {
            delete next[item.proposalId];
          }
          localDecisionsRef.current = next;
          return next;
        });
      } catch (error) {
        setHasError(true);
        throw error;
      } finally {
        setSubmittingAction(null);
      }
    },
    [handlers]
  );

  const buildResolveItem = useCallback(
    (
      proposal: ScoutDataChangeProposal,
      status: ScoutProposalResponseStatus,
      title?: string
    ): ResolveItem => {
      const resolvedTitle = title ?? formatDataChangeProposalTitle(proposal);
      if (status === "confirmed") {
        return {
          proposalId: proposal.proposalId,
          title: resolvedTitle,
          status,
          operation: proposal.operation,
        };
      }
      return {
        proposalId: proposal.proposalId,
        title: resolvedTitle,
        status,
      };
    },
    []
  );

  const decideOne = useCallback(
    async (proposal: ScoutDataChangeProposal, status: ScoutProposalResponseStatus) => {
      if (!handlers || isSubmitting || turnActionsDisabled) return;

      const title = formatDataChangeProposalTitle(proposal);
      const decision: LocalDecision = {
        status,
        title,
        ...(status === "confirmed" ? { operation: proposal.operation } : {}),
      };

      const nextLocal: Record<string, LocalDecision> = {
        ...localDecisionsRef.current,
        [proposal.proposalId]: decision,
      };
      localDecisionsRef.current = nextLocal;
      setLocalDecisions(nextLocal);

      const remainingPending = proposals.filter((candidate) => {
        if (handlers.respondedProposals[candidate.proposalId]) return false;
        return nextLocal[candidate.proposalId] === undefined;
      });

      // Sibling cards still open — park this decision locally; do not send yet.
      if (remainingPending.length > 0) {
        return;
      }

      const toFlush: ResolveItem[] = [];
      for (const candidate of proposals) {
        if (handlers.respondedProposals[candidate.proposalId]) continue;
        const local = nextLocal[candidate.proposalId];
        if (!local) continue;
        toFlush.push(buildResolveItem(candidate, local.status, local.title));
      }
      await flushResolutions(toFlush);
    },
    [buildResolveItem, flushResolutions, handlers, isSubmitting, proposals, turnActionsDisabled]
  );

  const resolveRemaining = async (status: ScoutProposalResponseStatus) => {
    if (!handlers || isSubmitting || turnActionsDisabled || pendingProposals.length === 0) return;
    setHasError(false);
    setSubmittingAction(status === "confirmed" ? "confirm-all" : "decline-all");

    const priorLocal = localDecisionsRef.current;
    const nextLocal: Record<string, LocalDecision> = { ...priorLocal };
    for (const proposal of pendingProposals) {
      nextLocal[proposal.proposalId] = {
        status,
        title: formatDataChangeProposalTitle(proposal),
        ...(status === "confirmed" ? { operation: proposal.operation } : {}),
      };
    }
    localDecisionsRef.current = nextLocal;
    setLocalDecisions(nextLocal);

    try {
      const toFlush: ResolveItem[] = [];
      for (const candidate of proposals) {
        if (handlers.respondedProposals[candidate.proposalId]) continue;
        const local = nextLocal[candidate.proposalId];
        if (!local) continue;
        toFlush.push(buildResolveItem(candidate, local.status, local.title));
      }
      await handlers.onResolve({ proposals: toFlush });
      setLocalDecisions((current) => {
        const next = { ...current };
        for (const item of toFlush) {
          delete next[item.proposalId];
        }
        localDecisionsRef.current = next;
        return next;
      });
    } catch {
      setHasError(true);
      localDecisionsRef.current = priorLocal;
      setLocalDecisions(priorLocal);
    } finally {
      setSubmittingAction(null);
    }
  };

  const handleAcceptAll = () => resolveRemaining("confirmed");
  const handleRejectAll = () => resolveRemaining("declined");

  if (proposals.length === 0) {
    return null;
  }

  return (
    <Box
      data-testid="scout-data-change-proposal-stack"
      sx={{
        mt: 1,
        maxWidth: 720,
      }}
    >
      <Stack spacing={2}>
        {proposals.map((proposal) => (
          <ScoutDataChangeProposalCard
            key={proposal.proposalId}
            proposal={proposal}
            responseStatus={getStatus(proposal.proposalId)}
            hideActions={isReadOnly}
            actionsDisabled={isSubmitting || turnActionsDisabled}
            canUndo={
              !stackUndo.undoAllBusy && stackUndo.eligibleUndoIds.includes(proposal.proposalId)
            }
            undoInProgress={
              !stackUndo.undoAllBusy && stackUndo.inFlightUndoIds.includes(proposal.proposalId)
            }
            undoFailure={
              stackUndo.batchReportedUndoIds.includes(proposal.proposalId)
                ? undefined
                : persistedUndoFailure(summariesById.get(proposal.proposalId))
            }
            onUndo={
              handlers?.onUndo && !stackUndo.undoAllBusy
                ? () => stackUndo.undoOne(proposal.proposalId)
                : undefined
            }
            onRetryStaleUndo={
              isReadOnly || !retryStaleUndo || stackUndo.undoAllBusy
                ? undefined
                : () => retryStaleUndo([proposal.proposalId])
            }
            onAccept={() => decideOne(proposal, "confirmed")}
            onReject={() => decideOne(proposal, "declined")}
            actors={
              isReadOnly ? staffHistoryActors(summariesById.get(proposal.proposalId)) : undefined
            }
          />
        ))}
        {showStackActions ? (
          <Stack
            direction="row"
            spacing={1.5}
            alignItems="center"
            justifyContent="flex-end"
            useFlexGap
            sx={{ flexWrap: "wrap", pr: 2 }}
          >
            <Button
              onClick={handleRejectAll}
              disabled={isLockedOut}
              startIcon={
                submittingAction === "decline-all" ? (
                  <CircularProgress size={14} color="inherit" />
                ) : undefined
              }
            >
              Reject all ({pendingProposals.length})
            </Button>
            <Button
              variant="contained"
              onClick={handleAcceptAll}
              disabled={isLockedOut}
              startIcon={
                submittingAction === "confirm-all" ? (
                  <CircularProgress size={14} color="inherit" />
                ) : undefined
              }
            >
              Accept all ({pendingProposals.length})
            </Button>
          </Stack>
        ) : null}
        {hasError ? (
          <Alert severity="error" onClose={() => setHasError(false)}>
            Something went wrong responding to these changes. Please try again.
          </Alert>
        ) : null}
        {stackUndo.batchUndoDisplay ? (
          <ScoutDataChangeUndoFailureAlert
            display={stackUndo.batchUndoDisplay}
            onRetryStaleUndo={
              isReadOnly || !retryStaleUndo || stackUndo.batchStaleRetryIds.length === 0
                ? undefined
                : () => retryStaleUndo(stackUndo.batchStaleRetryIds)
            }
            retryDisabled={isLockedOut}
            onDismiss={stackUndo.dismissBatchUndoError}
          />
        ) : null}
        {stackUndo.showUndoAll || stackUndo.hasHiddenBatchUndoError ? (
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            {stackUndo.hasHiddenBatchUndoError ? (
              <ShowUndoErrorButton onShow={stackUndo.showBatchUndoError} />
            ) : null}
            {stackUndo.showUndoAll ? (
              <Button
                size="small"
                onClick={() => {
                  void stackUndo.undoAll();
                }}
                disabled={isLockedOut}
                aria-busy={stackUndo.undoAllBusy}
                startIcon={
                  stackUndo.undoAllBusy ? <CircularProgress size={14} color="inherit" /> : undefined
                }
              >
                {stackUndo.undoAllBusy
                  ? "Undoing..."
                  : formatUndoChangesLabel(stackUndo.eligibleUndoIds.length)}
              </Button>
            ) : null}
          </Stack>
        ) : null}
      </Stack>
    </Box>
  );
}
